import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAppQueryClient } from "../../../lib/queryClient";
import { useBlulogContext } from "./context";
import {
  createBlulogFileUrl,
  fetchArticles,
  fetchCountries,
  fetchOverview,
  fetchTrackingDetails,
  type OverviewRow,
} from "./hooks";
import { normaliseText } from "./utils";
import {
  buildChartSeries,
  splitChartSeries,
  mergeSamples,
  computeSeriesDomain,
} from "./charting";
import type { CombinedSample, ChartSeries } from "./charting";
import { BlulogFilters } from "./BlulogFilters";
import { BlulogOverviewTable } from "./BlulogOverviewTable";
import { BlulogSelectionSummary } from "./BlulogSelectionSummary";
import { BlulogCharts, type DashboardTab } from "./BlulogCharts";
import type { Extent, Viewport } from "../../../lib/chartViewport";
import { clampViewport } from "../../../lib/chartViewport";

const DEFAULT_YEARS = ["2019", "2020", "2021", "2022", "2023", "2024", "2025"];

type TrackingChartData = {
  sensorSeries: ChartSeries[];
  expeditionSeries: ChartSeries[];
  sensorChart: CombinedSample[];
  expeditionChart: CombinedSample[];
  sensorDomain: ReturnType<typeof computeSeriesDomain>;
  expeditionDomain: ReturnType<typeof computeSeriesDomain>;
};

const EMPTY_TRACKING_DETAILS = { sensors: [], expeditions: [], startDate: null, endDate: null };

export function BlulogDashboardPage() {
  const queryClient = useAppQueryClient();
  const { dataSources } = useBlulogContext();
  const { prefix, tableKey, countriesKey, articlesKey, refreshVersion } = dataSources;

  const [trackingFilter, setTrackingFilter] = useState("");
  const [articleFilter, setArticleFilter] = useState("");
  const [selectedYears, setSelectedYears] = useState<string[]>(DEFAULT_YEARS);
  const [yearsInitialised, setYearsInitialised] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState<string | null>(null);
  const [tooltipFontSize, setTooltipFontSize] = useState(16);
  const [activeTab, setActiveTab] = useState<DashboardTab>("curves");
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const tableUrl = useMemo(() => createBlulogFileUrl(prefix, tableKey), [prefix, tableKey]);
  const countriesUrl = useMemo(() => createBlulogFileUrl(prefix, countriesKey), [prefix, countriesKey]);
  const articlesUrl = useMemo(() => createBlulogFileUrl(prefix, articlesKey), [prefix, articlesKey]);

  const overviewQuery = useQuery(
    {
      queryKey: ["blulog", "overview", prefix, tableKey, refreshVersion],
      queryFn: () => fetchOverview(tableUrl),
      staleTime: 60_000,
    },
    queryClient,
  );

  const countriesQuery = useQuery(
    {
      queryKey: ["blulog", "countries", prefix, countriesKey, refreshVersion],
      queryFn: () => fetchCountries(countriesUrl),
      staleTime: 60_000,
    },
    queryClient,
  );

  const articlesQuery = useQuery(
    {
      queryKey: ["blulog", "articles", prefix, articlesKey, refreshVersion],
      queryFn: () => fetchArticles(articlesUrl),
      staleTime: 60_000,
    },
    queryClient,
  );

  const overviewRows = overviewQuery.data ?? [];

  const availableYears = useMemo(() => buildAvailableYears(overviewRows), [overviewRows]);

  useEffect(() => {
    if (!yearsInitialised && availableYears.length) {
      setSelectedYears(availableYears);
      setYearsInitialised(true);
    }
  }, [availableYears, yearsInitialised]);

  const filteredRows = useMemo(
    () =>
      filterOverviewRows({
        rows: overviewRows,
        trackingFilter,
        articleFilter,
        selectedYears,
      }),
    [overviewRows, trackingFilter, articleFilter, selectedYears],
  );

  useEffect(() => {
    if (!filteredRows.length) {
      setSelectedTracking(null);
      return;
    }
    if (!selectedTracking || !filteredRows.some((row) => row.trackingNumber === selectedTracking)) {
      setSelectedTracking(filteredRows[0].trackingNumber);
    }
  }, [filteredRows, selectedTracking]);

  const selectedRow = filteredRows.find((row) => row.trackingNumber === selectedTracking) ?? null;

  const trackingQuery = useQuery(
    {
      queryKey: [
        "blulog",
        "tracking",
        prefix,
        selectedRow?.trackingNumber ?? null,
        selectedRow?.expeditionCount ?? 0,
        refreshVersion,
      ],
      queryFn: () =>
        fetchTrackingDetails(prefix, selectedRow?.trackingNumber ?? "", selectedRow?.expeditionCount ?? 0),
      // Avoid re-fetching on navigation/zoom: keep data warm until explicit invalidation.
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: Boolean(selectedRow?.trackingNumber),
    },
    queryClient,
  );

  const trackingDetails = trackingQuery.data ?? EMPTY_TRACKING_DETAILS;

  const chartData = useMemo<TrackingChartData>(() => {
    const series = buildChartSeries(trackingDetails);
    const { sensors, expeditions } = splitChartSeries(series);
    return {
      sensorSeries: sensors,
      expeditionSeries: expeditions,
      sensorChart: mergeSamples(sensors),
      expeditionChart: mergeSamples(expeditions),
      sensorDomain: computeSeriesDomain(sensors),
      expeditionDomain: computeSeriesDomain(expeditions),
    };
  }, [trackingDetails]);

  const topCountries = useMemo(() => {
    const countries = countriesQuery.data ?? [];
    return [...countries].sort((a, b) => b.count - a.count).slice(0, 12);
  }, [countriesQuery.data]);

  const articleHistogram = useMemo(
    () => buildArticleHistogram(articlesQuery.data ?? null, overviewRows),
    [articlesQuery.data, overviewRows],
  );

  const trackingRange = useMemo(
    () => ({
      start: trackingDetails.startDate ?? null,
      end: trackingDetails.endDate ?? null,
    }),
    [trackingDetails.startDate, trackingDetails.endDate],
  );

  const extent = useMemo<Extent | null>(() => {
    const timestamps: number[] = [];
    chartData.sensorChart.forEach((item) => {
      if (Number.isFinite(item.timestamp)) timestamps.push(item.timestamp);
    });
    chartData.expeditionChart.forEach((item) => {
      if (Number.isFinite(item.timestamp)) timestamps.push(item.timestamp);
    });
    if (!timestamps.length) return null;
    return { min: Math.min(...timestamps), max: Math.max(...timestamps) };
  }, [chartData.sensorChart, chartData.expeditionChart]);

  useEffect(() => {
    if (!extent) {
      setViewport(null);
      return;
    }
    setViewport((previous) => {
      if (!previous) {
        return { start: extent.min, end: extent.max };
      }
      const clamped = clampViewport(previous, extent);
      if (clamped.start === previous.start && clamped.end === previous.end) {
        return previous;
      }
      return clamped;
    });
  }, [extent?.min, extent?.max]);

  return (
    <div className={`blulog-dashboard${filtersOpen ? "" : " blulog-dashboard--collapsed"}`}>
      <BlulogFilters
        tooltipFontSize={tooltipFontSize}
        onTooltipFontChange={setTooltipFontSize}
        trackingFilter={trackingFilter}
        onTrackingChange={setTrackingFilter}
        articleFilter={articleFilter}
        onArticleChange={setArticleFilter}
        availableYears={availableYears}
        selectedYears={selectedYears}
        onYearToggle={(year) => setSelectedYears((previous) => toggleYear(previous, year))}
        onReset={() => {
          setTrackingFilter("");
          setArticleFilter("");
          setSelectedYears(availableYears);
        }}
        isOpen={filtersOpen}
        onToggle={setFiltersOpen}
      />

      <div className="blulog-main">
        <BlulogOverviewTable
          rows={filteredRows}
          selectedTracking={selectedTracking}
          onSelect={setSelectedTracking}
          isLoading={overviewQuery.isLoading}
        />

        <BlulogSelectionSummary row={selectedRow} />

        <BlulogCharts
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tooltipFontSize={tooltipFontSize}
          sensorSeries={chartData.sensorSeries}
          expeditionSeries={chartData.expeditionSeries}
          sensorChartData={chartData.sensorChart}
          expeditionChartData={chartData.expeditionChart}
          sensorDomain={chartData.sensorDomain}
          expeditionDomain={chartData.expeditionDomain}
          trackingRange={trackingRange}
          isTrackingLoading={trackingQuery.isLoading}
          topCountries={topCountries}
          countriesLoading={countriesQuery.isLoading}
          articleHistogram={articleHistogram}
          articlesLoading={articlesQuery.isLoading}
          extent={extent}
          viewport={viewport}
          onViewportChange={setViewport}
        />
      </div>
    </div>
  );
}

function buildAvailableYears(rows: OverviewRow[]) {
  const set = new Set<string>();
  rows.forEach((row) => {
    if (row.lastMeasure) {
      set.add(row.lastMeasure.getFullYear().toString());
    }
  });
  DEFAULT_YEARS.forEach((year) => set.add(year));
  return Array.from(set).sort();
}

function filterOverviewRows({
  rows,
  trackingFilter,
  articleFilter,
  selectedYears,
}: {
  rows: OverviewRow[];
  trackingFilter: string;
  articleFilter: string;
  selectedYears: string[];
}) {
  if (!rows.length) return [];
  const trackingTerm = normaliseText(trackingFilter);
  const articleTerm = normaliseText(articleFilter);
  const allowedYears = new Set(selectedYears);

  return rows.filter((row) => {
    const matchesTracking = !trackingTerm || normaliseText(row.trackingNumber).includes(trackingTerm);
    const matchesArticle = !articleTerm || normaliseText(row.articleEi).includes(articleTerm);
    const year = row.lastMeasure?.getFullYear().toString();
    const matchesYear = allowedYears.size === 0 || (year ? allowedYears.has(year) : false);
    return matchesTracking && matchesArticle && matchesYear;
  });
}

function toggleYear(list: string[], year: string) {
  if (list.includes(year)) {
    return list.filter((value) => value !== year);
  }
  return [...list, year];
}

function buildArticleHistogram(
  remoteArticles: Array<{ article: string; count: number }> | null,
  overviewRows: OverviewRow[],
) {
  if (remoteArticles && remoteArticles.length) {
    return [...remoteArticles].sort((a, b) => b.count - a.count).slice(0, 15);
  }

  const counts = new Map<string, number>();
  overviewRows.forEach((row) => {
    const key = row.articleEi || "N/A";
    counts.set(key, (counts.get(key) ?? 0) + row.expeditionCount);
  });

  return Array.from(counts.entries())
    .map(([article, count]) => ({ article, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15);
}
